import { Tabs } from "antd"
import React from "react"
import NotificationsSettings from "./components/NotificationsSettings.container"
import SettingSection from "@/modules/shared/SettingSection"
import { useTranslation } from "react-i18next"
import { hasEnableNotificationsAtom, userAtom } from "@/atoms/userAtom"
import { useRecoilValue } from "recoil"
import { useOrg } from "@/hooks/useOrg"
import {
  NotifiedStatusesTypes,
  Transporters,
  TransportersSettingsType,
} from "@/types/organizations.types"
import { OrderStatusesTypes } from "@/types/statuses.types"
import { MediumText } from "@/UI"

const NotificationsTabs: React.FC<{}> = () => {
  const { t } = useTranslation()

  const { preference } = useRecoilValue(userAtom)
  const enableNotifications = useRecoilValue(hasEnableNotificationsAtom)
  const { notificationsConfig, notificationsSettings } = preference
  const { updateOrgPreferences, isLoading } = useOrg()

  const {
    merchantNotifications,
    merchantTransporters,
    customerNotifications,
    customerTransporters,
  } = notificationsSettings

  const updateTransportersSettings = async (
    transporter: Transporters,
    value: boolean,
    type: "merchant" | "customer"
  ) => {
    const newTransporterSettings =
      type === "merchant"
        ? { ...notificationsSettings?.merchantTransporters }
        : { ...notificationsSettings?.customerTransporters }
    newTransporterSettings[transporter] = value
    switch (transporter) {
      case Transporters.SMS:
        newTransporterSettings.whatsapp =
          newTransporterSettings.whatsapp === true && newTransporterSettings.sms === true
            ? false
            : newTransporterSettings.whatsapp
        break
      case Transporters.WHATSAPP:
        newTransporterSettings.sms =
          newTransporterSettings.whatsapp === true && newTransporterSettings.sms === true
            ? false
            : newTransporterSettings.sms
        break
      default:
        break
    }

    let newPreference = { ...preference }
    if (type === "merchant") {
      newPreference.notificationsSettings = {
        ...newPreference.notificationsSettings,
        merchantTransporters: newTransporterSettings as TransportersSettingsType,
      }
    } else {
      newPreference.notificationsSettings = {
        ...newPreference.notificationsSettings,
        customerTransporters: newTransporterSettings as TransportersSettingsType,
      }
    }

    try {
      await updateOrgPreferences(newPreference)
    } catch (error) {
      console.log(error)
    }
  }

  const updateStatusNotification = async (
    status: OrderStatusesTypes,
    value: boolean,
    type: "merchant" | "customer"
  ) => {
    try {
      let newPreference = { ...preference }
      if (type === "merchant") {
        newPreference.notificationsSettings = {
          ...newPreference.notificationsSettings,
          merchantNotifications: {
            ...newPreference.notificationsSettings.merchantNotifications,
            [status]: value,
          } as NotifiedStatusesTypes,
        }
      } else {
        newPreference.notificationsSettings = {
          ...newPreference.notificationsSettings,
          customerNotifications: {
            ...newPreference.notificationsSettings.customerNotifications,
            [status]: value,
          } as NotifiedStatusesTypes,
        }
      }
      await updateOrgPreferences(newPreference)
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <SettingSection title={t("profile.NotificationsSettings.notifications")}>
      {!enableNotifications && (
        <div className="info-container border-amber-400 border-b-2 p-2">
          <div className="flex flex-col space-y-4">
            <MediumText>{t("profile.NotificationsSettings.notificationsEnableInfo")}</MediumText>
          </div>
        </div>
      )}
      <Tabs
        className="w-full !p-0 notifications-tab"
        destroyInactiveTabPane
        defaultActiveKey="0"
        items={[
          {
            key: "0",
            label: t("common.recipient"),
            children: (
              <NotificationsSettings
                isRecipients={true}
                notifications={customerNotifications}
                transporters={customerTransporters}
                notificationsConfig={notificationsConfig.merchantNotifications}
                transportersConfig={notificationsConfig.merchantTransporters}
                isLoading={isLoading}
                updateNotifications={(status, value) =>
                  updateStatusNotification(status, value, "customer")
                }
                updateTransporters={(transporter, value) =>
                  updateTransportersSettings(transporter, value, "customer")
                }
                disabled={!enableNotifications}
              />
            ),
          },
          {
            key: "1",
            label: t("common.sender"),
            children: (
              <NotificationsSettings
                isRecipients={false}
                notifications={merchantNotifications}
                transporters={merchantTransporters}
                notificationsConfig={notificationsConfig.customerNotifications}
                transportersConfig={notificationsConfig.customerTransporters}
                isLoading={isLoading}
                updateNotifications={(status, value) =>
                  updateStatusNotification(status, value, "merchant")
                }
                updateTransporters={(transporter, value) =>
                  updateTransportersSettings(transporter, value, "merchant")
                }
                disabled={!enableNotifications}
              />
            ),
          },
        ]}
      />
    </SettingSection>
  )
}

export default NotificationsTabs
